<script setup lang="ts">
  import RecipientCard from '@/components/drawer/DrawerRecipients/RecipientCard.vue'
  import { useElementOption } from '@/composable/elementOptions'
  import { useRecipients } from '@/composition'
  import { useBuilderStore } from '@/store/builder'
  import { CheckBoxElementOptions, ELEMENTS_META, IElement, renderLabelAvatarOnSelect } from '@gohighlevel/ghl-proposals-common'
  import { UICheckbox, UIDivider, UIFormItem, UISelect } from '@gohighlevel/ghl-ui'
  import { PropType, computed, onMounted, ref } from 'vue'
  const { updateOption } = useElementOption()
  const selectValue = ref()
  const recipient = ref()
  const searchQuery = ref('')
  const { syncRecipient } = useBuilderStore()
  const { signedByOptionsWithStaff, getSelectValue, fetchRecipientForElementOrGroup, assignRecipientToFillableElement } = useRecipients()
  const props = defineProps({
    element: Object as PropType<IElement<CheckBoxElementOptions>>,
  })
  const assignRecipientToCheckbox = (val: string) => {
    if (val && props.element) {
      assignRecipientToFillableElement(val, props.element)
      syncRecipientList()
    }
  }
  const syncRecipientList = () => {
    syncRecipient()
    if (props.element) {
      const assignedRecipient = fetchRecipientForElementOrGroup(props.element)
      if (assignedRecipient) {
        recipient.value = assignedRecipient
      }
      selectValue.value = getSelectValue(props.element)
    }
  }

  const updateCheckbox = (val: boolean) => {
    updateOption(ELEMENTS_META.PRE_CHECKED, val, props.element?.id)
    updateOption(ELEMENTS_META.TEXT, val.toString(), props.element?.id)
  }

  onMounted(() => {
    syncRecipientList()
  })

  const filteredSignedByOptions = computed(() => {
    if (!searchQuery.value || searchQuery.value.trim() === '') {
      return signedByOptionsWithStaff.value
    }
    const query = searchQuery.value.toLowerCase().trim()
    return signedByOptionsWithStaff.value.filter((option: any) => {
      const label = option.label?.toLowerCase() || ''
      return label.includes(query)
    })
  })

  const handleSearch = (query: string) => {
    searchQuery.value = query
  }
</script>

<template>
  <div class="hl-proposal-sidebar-content hl-proposal-checkbox-properties">
    <div class="mb-4">
      <UIFormItem :label="$t('common.toBeFilledBy')">
        <UISelect
          :id="`checkbox-text`"
          :placeholder="$t('common.assignRecipientToCheckbox')"
          :options="filteredSignedByOptions"
          :value="selectValue"
          filterable
          @update:value="(val: any) => assignRecipientToCheckbox(val)"
          @search="handleSearch"
          :render-label="renderLabelAvatarOnSelect"
        />
      </UIFormItem>
      <div>
        <RecipientCard :recipient="recipient" />
      </div>
    </div>
    <UIDivider />

    <div class="my-6">
      <div class="flex flex-col">
        <div class="py-2">
          <UICheckbox
            :id="`${element?.id}-required-checkbox`"
            :checked="element?.component.options.required"
            :type="'default'"
            @update:checked="
              (val: boolean) => {
                updateOption(ELEMENTS_META.REQUIRED, val, element?.id)
              }
            "
          >
            {{ $t('common.required') }}
          </UICheckbox>
        </div>
        <div class="py-2">
          <UICheckbox
            :id="`${element?.id}-pre-checked-checkbox`"
            :checked="element?.component.options?.preChecked"
            :type="'default'"
            @update:checked="updateCheckbox"
          >
            {{ $t('common.preChecked') }}
          </UICheckbox>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
  .hl-proposal-checkbox-properties {
    .n-divider.n-divider--no-title {
      margin: 10px 0px;
    }
    .select-field-button {
      width: 100%;
      border: 1px solid #eff4ff;
    }
  }
</style>
